import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { instructors as instructorsApi } from '@/api/endpoints'
import { EmptyState, Field, Select, Spinner } from '@/components/ui'
import MultiSelect from '@/components/ui/MultiSelect'
import { formatMoney, minutesToHours } from '@/lib/utils'

import RangePicker from './RangePicker'
import { useRewardRange, useTaughtOnly } from './useRewardRange'
import { CenterFilter, useCenterFilter } from '@/features/centers/useCenters'

/**
 * „Odmeny a výplatné listy“ - prehľad odmien za obdobie po inštruktoroch;
 * klik na meno otvorí výplatný list (`/rewards/:id`).
 *
 * Filter: obdobie, inštruktori (`?instructors=1,2`) a čo rátať - potvrdené
 * lekcie, alebo len tie, ktoré inštruktor v mobile označil ako odučené
 * (`?taught=1`). Všetko je v adrese, takže odkaz na výplatný list aj späť
 * nesie ten istý filter.
 */
export default function RewardsPage() {
  const { t } = useTranslation()
  const [range, setRange] = useRewardRange()
  const [taughtOnly, setTaughtOnly] = useTaughtOnly()
  const [params, setParams] = useSearchParams()
  const { from, to } = range

  const selected = (params.get('instructors') ?? '')
    .split(',')
    .map(Number)
    .filter((id) => id > 0)
  const setSelected = (ids: number[]) =>
    setParams(
      (current) => {
        const copy = new URLSearchParams(current)
        if (ids.length) copy.set('instructors', ids.join(','))
        else copy.delete('instructors')
        return copy
      },
      { replace: true },
    )
  const toggle = (id: number | string) =>
    setSelected(selected.includes(Number(id)) ? selected.filter((x) => x !== Number(id)) : [...selected, Number(id)])

  const { data: instructors } = useQuery({
    queryKey: ['instructors', 'reward-filter'],
    queryFn: () => instructorsApi.list({ limit: 200, ordering: 'sname,fname' }),
    staleTime: 60_000,
  })
  const options = (instructors?.results ?? []).map((i) => ({ id: i.id, label: i.full_name }))

  // Strediská (`sql/040`) - odmeny z blokov vybraných stredísk.
  const [centerIds] = useCenterFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['rewards-overview', from, to, selected.join(','), taughtOnly, centerIds.join(',')],
    queryFn: () =>
      instructorsApi.rewardsOverview(from || undefined, to || undefined, selected, taughtOnly, centerIds),
    placeholderData: (previous) => previous,
  })

  const grandTotal = (data?.rows ?? []).reduce(
    (sum, row) => sum + Number.parseFloat(row.summary.total || '0'),
    0,
  )
  const grandUnpaid = (data?.rows ?? []).reduce(
    (sum, row) => sum + Number.parseFloat(row.summary.unpaid || '0'),
    0,
  )
  // Na výplatu = nevyplatené a ešte nie na výplatnom liste (`sql/039`).
  const grandToPay = (data?.rows ?? []).reduce(
    (sum, row) => sum + Number.parseFloat(row.summary.to_pay ?? row.summary.unpaid ?? '0'),
    0,
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="text-lg font-semibold">{t('nav.rewards')}</h1>
        <RangePicker value={range} onChange={setRange} />
        <div className="ml-auto flex gap-4 text-sm">
          <div>
            <div className="text-xs uppercase text-slate-500">{t('global.total')}</div>
            <div className="text-lg font-semibold">{formatMoney(grandTotal)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">{t('rewards.unpaid')}</div>
            <div className="text-lg font-semibold">{formatMoney(grandUnpaid)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">{t('payroll.toPay')}</div>
            <div className="text-lg font-semibold text-amber-600">{formatMoney(grandToPay)}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('nav.instructors')} className="w-72 max-w-full">
          <MultiSelect
            options={options}
            selected={new Set(selected)}
            onToggle={toggle}
            onSelectAll={(ids) => setSelected([...new Set([...selected, ...ids.map(Number)])])}
            onClearAll={() => setSelected([])}
            placeholder={t('rewards.allInstructors')}
            searchThreshold={0}
          />
        </Field>
        <CenterFilter className="w-56" />
        <Field label={t('units.countWhat')} className="w-64 max-w-full">
          <Select value={taughtOnly ? 'taught' : 'confirmed'} onChange={(e) => setTaughtOnly(e.target.value === 'taught')}>
            <option value="confirmed">{t('units.countConfirmed')}</option>
            <option value="taught">{t('units.countTaught')}</option>
          </Select>
        </Field>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.rows.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('reservation.instructor')}</th>
                <th className="text-right">{t('statistics.totalHours')}</th>
                <th className="text-right">{t('rewards.paid')}</th>
                <th className="text-right">{t('rewards.unpaid')}</th>
                <th className="text-right">{t('global.total')}</th>
                <th className="text-right">{t('payroll.toPay')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.rows.map(({ instructor, summary }) => (
                <tr key={instructor.id}>
                  <td>
                    <Link
                      // Celý filter ide so sebou - výplatný list ukáže to isté
                      // obdobie a „len odučené“, šípka späť vráti výber.
                      to={{ pathname: `/rewards/${instructor.id}`, search: params.toString() ? `?${params}` : '' }}
                      className="font-medium text-brand-600 hover:underline"
                    >
                      {instructor.full_name}
                    </Link>
                  </td>
                  <td className="text-right tabular-nums text-slate-600">
                    {minutesToHours(summary.minutes ?? 0)}
                  </td>
                  <td className="text-right tabular-nums text-green-600">
                    {formatMoney(summary.paid)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatMoney(summary.unpaid)}
                  </td>
                  <td className="text-right font-semibold tabular-nums">
                    {formatMoney(summary.total)}
                  </td>
                  <td className="text-right font-semibold tabular-nums text-amber-600">
                    {formatMoney(summary.to_pay ?? summary.unpaid)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
